import React, { useState, useEffect, useRef } from 'react';
import {
  useGetAiWorkersBySourceTypeQuery,
  useActionUploadInternalActionMutation,
  useSaveAiWorkerUsageMutation,
  useGetAiWorkersUsageByCategoryMutation,
  useGetAiworkersPresignedUrlMutation,
  useUploadFileToS3Mutation,
} from '@circuitry-ai/doc-data';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { v4 as uuidv4 } from 'uuid';
import CtyPromptInput, { promptInputType, ErrorBoundary, UploadedFileType, CtyShowToast, checkType, UXGenieTemplateRenderer, CtyButton } from '@circuitry-ai/doc-ui';
import { startAgentStream, streamAgentChat } from '../../../../modules/chatadvisor/src/lib/agentconverse/agentstreamingservice';
import { ResizableSplitPanel, WorkflowHtmlPreview } from './shared';
import { CommentModal } from './shared';
import { useTemplateState } from '../hooks/useTemplateState';
import {
  cleanGeneratedCode,
  formatUTCDateTime,
  safeParsePayload,
  getApiSpec,
  findTemplatedFields,
  createNestedPayloadRecursive,
  getFileType,
  isJSXCode,
  findJSXCodeInObject,
  extractWorkflowHtmlOutput,
  validateWorkflowHtml,
  isHtmlContent,
  encodeToBase64,
} from '../utils';


const CtyPromptInputWithErrorBoundary = ErrorBoundary(CtyPromptInput);

export interface UXGenieSplitPanelProps {
  isEdit: boolean;
  isReadOnly?: boolean;
  initialCode?: string;
  initialData?: string;
  initialActionName?: string | null;
  initialActionId?: string | null;
  initialUsageId?: string | null;
  initialActionData?: any;
  token: string;
  worker?: any;
  onSaveComplete: (workerId: string) => void;
}

export const UXGenieSplitPanel: React.FC<UXGenieSplitPanelProps> = ({
  isEdit,
  isReadOnly = false,
  initialCode,
  initialData,
  initialActionName,
  initialActionId,
  initialActionData,
  token,
  worker,
  onSaveComplete,
}) => {
  const {
    generatedCode, setGeneratedCode,
    inputJson, setInputJson,
    messages, setMessages,
    interactionState, setInteractionState,
    instructionText, setInstructionText,
    elementComments, setElementComments,
    isSpeechMode, setIsSpeechMode,
    addElementComment, clearElementComments,
    resetInteraction,
    currentActionName, setCurrentActionName,
    currentActionId, setCurrentActionId,
    currentActionData, setCurrentActionData,
  } = useTemplateState();
  const [isEditMode, setIsEditMode] = useState(isEdit);
  const [hasGeneratedTemplate, setHasGeneratedTemplate] = useState(!!initialCode);
  const [codeHistory, setCodeHistory] = useState<Array<{ id: string; code: string; timestamp: string }>>(
    initialCode ? [{ id: 'initial', code: initialCode, timestamp: new Date().toISOString() }] : []
  );
  const [sessionId, setSessionId] = useState('');
  const streamStartedRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  // Preview & title state
  const [previewingCode, setPreviewingCode] = useState<string>(initialCode && !isHtmlContent(initialCode) ? initialCode : '');
  const [previewingHtml, setPreviewingHtml] = useState<string | null>(
    initialCode && isHtmlContent(initialCode) ? initialCode : null
  );
  const [generatedHtml, setGeneratedHtml] = useState<string | null>(
    initialCode && isHtmlContent(initialCode) ? initialCode : null
  );
  const [templateTitle, setTemplateTitle] = useState<string>(initialActionName || '');


  // Generation progress tracking (per message)
  const [generationProgress, setGenerationProgress] = useState<Record<string, {
    status: 'in_progress' | 'done' | 'error';
    nodes: string[];
    completedNodes: string[];
    errorMsg?: string;
  }>>({});
  const [messageTemplates, setMessageTemplates] = useState<Record<string, string>>({});
  const [messageHtmlTemplates, setMessageHtmlTemplates] = useState<Record<string, string>>({});

  // Save modal state
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const templateMetadataRef = useRef<Record<string, unknown>>({});
  const [pendingSaveData, setPendingSaveData] = useState<{
    code: string;
    originalData: string;
    actionName?: string;
    actionId?: string;
    isHtml?: boolean;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Action / file upload state
  const [selectedAction, setSelectedAction] = useState<any>(initialActionData || null);
  const [requiresFileUpload, setRequiresFileUpload] = useState(false);
  const [pendingInputVal, setPendingInputVal] = useState('');
  const [uploadedSourceId, setUploadedSourceId] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileType[]>([]);
  // Image upload state (standalone, not tied to an action)
  const [imageSourceId, setImageSourceId] = useState<string | null>(null);

  // API hooks
  const [saveAiWorkerUsage] = useSaveAiWorkerUsageMutation();
  const [getTemplatesByWorker] = useGetAiWorkersUsageByCategoryMutation();
  const [executeAction] = useActionUploadInternalActionMutation();
  const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
  const [uploadtos3] = useUploadFileToS3Mutation();

  const [skip, setSkip] = useState(!token);
  useEffect(() => {
    if (token && skip) setSkip(false);
  }, [token, skip]);
  const { data: actionsList } = useGetAiWorkersBySourceTypeQuery('action', { skip, refetchOnMountOrArgChange: true });

  // --- Initialize from props ---
  useEffect(() => {
    if (initialCode) {
      if (isHtmlContent(initialCode)) {
        setGeneratedHtml(initialCode);
        setPreviewingHtml(initialCode);
        setPreviewingCode('');
      } else {
        setGeneratedCode(initialCode);
        setPreviewingCode(initialCode);
      }
    }
    if (initialData) setInputJson(initialData);
    if (initialActionName) { setCurrentActionName(initialActionName); setTemplateTitle(initialActionName); }
    if (initialActionId) setCurrentActionId(initialActionId);
    if (initialActionData) {
      setCurrentActionData(initialActionData);
      setSelectedAction(initialActionData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Welcome message for fresh create mode
  useEffect(() => {
    if (!initialCode && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: (
          <div className="space-y-3">
            <p>What would you like to generate today?</p>
          </div>
        ),
        timestamp: new Date().toISOString(),
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // Initialize streaming session — re-runs when worker becomes available
  useEffect(() => {
    if (!worker?.aiworker_id) return;
    // Reset so we start a fresh session for this worker
    streamStartedRef.current = false;
    setSessionId('');
  }, [worker?.aiworker_id]);

  useEffect(() => {
    let isCancelled = false;
    const init = async () => {
      if (streamStartedRef.current || sessionId || !token || !worker?.aiworker_id) return;
      streamStartedRef.current = true;
      try {
        const baseUrl = import.meta.env.CTY_AGENTS_STREAM_URL;
        const result = await startAgentStream({ baseUrl, aiworker_id: worker.aiworker_id, token });
        const session_id = result?.session_id;
        if (session_id && !isCancelled) setSessionId(session_id);
        else streamStartedRef.current = false;
      } catch (error) {
        streamStartedRef.current = false;
        CtyShowToast(checkType.error, (error as Error)?.message ?? 'Failed to start agent stream');
      }
    };
    void init();
    return () => { isCancelled = true; };
  }, [token, worker?.aiworker_id, sessionId]);

  // File upload processing
  useEffect(() => {
    const processFileUpload = async () => {
      const fileToProcess = uploadedFiles.find(f => f.uploading);
      if (!fileToProcess || isProcessingFile) return;

      const actionToUse = selectedAction || currentActionData;
      setIsProcessingFile(true);
      const file = fileToProcess.file;

      // For standalone image uploads (not tied to an action), show progress in chat
      const uploadMsgId = !actionToUse ? uuidv4() : null;
      if (uploadMsgId) {
        setMessages(prev => [...prev, {
          id: uploadMsgId,
          role: 'assistant',
          content: (
            <div className="flex items-center gap-2 text-sm text-default-600">
              <Icon icon="eos-icons:loading" className="animate-spin shrink-0" width={16} />
              <span>Uploading <span className="font-medium">{file.name}</span>...</span>
            </div>
          ),
          timestamp: new Date().toISOString(),
        }]);
      }

      try {
        const uploadURL: any = await getAiworkersPresignedUrl([{ filename: file.name, source_type: 'action' }]).unwrap();
        if (!uploadURL?.[0]?.presigned_url) throw new Error('Failed to get presigned URL');
        await uploadtos3({ file, presignedUrl: uploadURL[0].presigned_url, CtyShowToast }).unwrap();

        if (actionToUse) {
          // Action file upload — store for action execution
          setUploadedSourceId(uploadURL[0].source_id);
          setUploadedFileName(file.name);
          CtyShowToast(checkType.success, 'File uploaded successfully. Click send to process.');
        } else {
          // Standalone image upload for UXGenie — store source_id and file name for meta_data
          setImageSourceId(uploadURL[0].source_id);
          setUploadedFileName(file.name);
          if (uploadMsgId) {
            setMessages(prev => prev.map(msg => msg.id === uploadMsgId ? {
              ...msg,
              content: (
                <div className="flex items-center gap-2 text-sm text-[#067647]">
                  <Icon icon="material-symbols:check-circle-rounded" className="shrink-0" width={16} />
                  <span><span className="font-medium">{file.name}</span> uploaded. Send your message to include it.</span>
                </div>
              ),
            } : msg));
          }
        }

        setUploadedFiles(prev => prev.map(f => f.name === file.name ? { ...f, uploading: false } : f));
      } catch (error) {
        if (uploadMsgId) {
          setMessages(prev => prev.map(msg => msg.id === uploadMsgId ? {
            ...msg,
            content: (
              <div className="flex items-center gap-2 text-sm text-danger">
                <Icon icon="material-symbols:error-outline" className="shrink-0" width={16} />
                <span>Failed to upload image: {(error as Error)?.message || 'Unknown error'}</span>
              </div>
            ),
          } : msg));
        } else {
          CtyShowToast(checkType.error, (error as Error)?.message || 'Failed to upload file');
        }
        setUploadedFiles(prev => prev.map(f => f.name === file.name ? { ...f, uploading: false } : f));
      } finally {
        setIsProcessingFile(false);
      }
    };
    processFileUpload();
  }, [uploadedFiles, selectedAction, currentActionData, isProcessingFile]);

  // --- Helpers ---
  const checkIfActionRequiresFileUpload = (action: any) => {
    const apiSpec = getApiSpec(action);
    const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;
    if (!samplePayload) return false;
    return !!safeParsePayload(samplePayload).source_extension;
  };

  const findActionInText = (text: string) => {
    if (!actionsList || !text) return null;
    const sorted = [...actionsList].sort((a: any, b: any) => b.name.length - a.name.length);
    for (const action of sorted) {
      if (text.toLowerCase().startsWith(`@${action.name}`.toLowerCase())) return action;
      if (action.name.length > 5 && text.toLowerCase().includes(action.name.toLowerCase())) return action;
    }
    return null;
  };

  const formatElementCommentsAsText = (comments: typeof elementComments): string => {
    if (comments.length === 0) return '';
    const formatted = comments.map((c, i) =>
      `${i + 1}. Element: "${c.elementText.substring(0, 50)}${c.elementText.length > 50 ? '...' : ''}"\n   Change: ${c.comment}`
    ).join('\n\n');
    return `Make the following changes:\n\n${formatted}`;
  };

  const getNextTemplateNumber = async (baseName: string, workerId: string): Promise<number> => {
    try {
      const response = await getTemplatesByWorker({ aiworker_id: workerId, aiworker_type: 'uxgenie_logs' }).unwrap();
      const templates = Array.isArray(response) ? response : response?.data || [];
      let maxNumber = 0;
      const pattern = new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*(\\d+)?$`, 'i');
      templates.forEach((t: any) => {
        const name = t?.output_data?.action_name || t?.agent_name || '';
        const match = name.match(pattern);
        if (match) {
          const num = match[1] ? parseInt(match[1], 10) : 0;
          if (num > maxNumber) maxNumber = num;
        }
      });
      return maxNumber + 1;
    } catch {
      return 1;
    }
  };

  // --- Handlers ---
  const handlePreviewClick = (e: React.MouseEvent, text: string) => {
    setInteractionState({ isActive: true, selectedText: text, position: { x: e.clientX, y: e.clientY } });
  };

  const handleAddComment = () => {
    if (!interactionState.selectedText || !instructionText.trim()) return;
    addElementComment(interactionState.selectedText, instructionText.trim());
    resetInteraction();
  };

  const handleApplyInstruction = () => {
    if (!interactionState.selectedText || !instructionText) return;
    handleGenerate(`Regarding "${interactionState.selectedText}": ${instructionText}`);
    resetInteraction();
  };

  const handleSaveTemplate = (codeOverride?: string, dataOverride?: string, actionNameOverride?: string, actionIdOverride?: string) => {
    const htmlToSave = previewingHtml || generatedHtml;
    const codeToSave = codeOverride || previewingCode || generatedCode || undefined;
    const contentToSave = htmlToSave || codeToSave;
    const isHtmlTemplate = !!htmlToSave || !!(contentToSave && isHtmlContent(contentToSave));
    if (!contentToSave) { alert('No code to save'); return; }
    const nameToUse = templateTitle || actionNameOverride || currentActionName || undefined;
    setPendingSaveData({
      code: contentToSave,
      originalData: dataOverride || inputJson || '',
      actionName: nameToUse,
      actionId: actionIdOverride || currentActionId || undefined,
      isHtml: isHtmlTemplate,
    });
    setTemplateNameInput(nameToUse || '');
    setIsSaveModalOpen(true);
  };

  const confirmSaveTemplate = async () => {
    if (!pendingSaveData) return;
    const workerId = worker?.aiworker_id;
    if (!workerId) {
      alert('No UXGenie Template selected. Please navigate from the UXGenie templates page.');
      return;
    }
    setIsSaving(true);
    try {
      let templateName = templateNameInput.trim();
      if (!templateName) {
        const baseName = worker?.name || 'Template';
        const nextNumber = await getNextTemplateNumber(baseName, workerId);
        templateName = `${baseName} ${nextNumber}`;
      }
      const usageId = uuidv4();
      const currentTime = new Date();

      let outputData: Record<string, any>;
      if (pendingSaveData.isHtml) {
        // WAF blocks large inline HTML in the save request body — upload the
        // HTML as a file to S3 instead and store only the file reference.
        const htmlFileName = `${usageId}.html`;
        const presignedResult: any = await getAiworkersPresignedUrl([{
          filename: htmlFileName,
          source_type: 'uxgenie',
          aiworker_id: workerId,
          aiworker_usage_id: usageId,
          metadata: {},
        }]).unwrap();
        const uploadUrl = presignedResult?.[0]?.presigned_url;
        const sourceId = presignedResult?.[0]?.source_id;
        if (!uploadUrl) throw new Error('Failed to get presigned URL for template upload');

        const htmlFile = new File([pendingSaveData.code], htmlFileName, { type: 'text/html' });
        await uploadtos3({ file: htmlFile, presignedUrl: uploadUrl, CtyShowToast }).unwrap();

        outputData = {
          output_type: 'html',
          action_name: templateName,
          saved_at: formatUTCDateTime(currentTime),
          storage: 's3',
          html_file_name: htmlFileName,
          source_id: sourceId,
        };
      } else {
        outputData = {
          generated_code: pendingSaveData.code,
          output_type: 'jsx',
          action_name: templateName,
          saved_at: formatUTCDateTime(currentTime),
        };
      }

      await saveAiWorkerUsage({
        aiworker_id: workerId,
        payLoad: {
          aiworker_id: workerId,
          aiworker_usage_id: usageId,
          aiworker_type: 'uxgenie_logs',
          used_at: formatUTCDateTime(currentTime),
          status: 'succeeded',
          input_data: { user_input: templateName, original_data: pendingSaveData.originalData },
          output_data: outputData,
          metadata: templateMetadataRef.current,
        }
      }).unwrap();
      setIsSaveModalOpen(false);
      setPendingSaveData(null);
      setTemplateNameInput('');
      CtyShowToast('success', `Template "${templateName}" saved successfully!`);
      onSaveComplete(workerId);
    } catch (error) {
      CtyShowToast('error', `Failed to save template: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerate = async (customInstructions: string, dataOverride?: string, actionDataOverride?: any, imageId?: string, imageFileName?: string) => {
    const messageId = uuidv4();
    const dataToUse = dataOverride || inputJson;
    const workerId = worker?.aiworker_id;
    const baseUrl = import.meta.env.CTY_AGENTS_STREAM_URL;

    // Add AI message placeholder
    setMessages(prev => [...prev, {
      id: messageId,
      role: 'assistant',
      content: 'Working on the changes',
      timestamp: new Date().toISOString(),
    }]);

    // Mark as in-progress
    setGenerationProgress(prev => ({ ...prev, [messageId]: { status: 'in_progress', nodes: [], completedNodes: [] } }));
    const streamedNodes: string[] = [];
    const streamedCompletedNodes: string[] = [];

    try {
      let finalCode = '';
      let finalHtml = '';
      let isCodeFound = false;
      let isHtmlFound = false;
      let textResponse = '';
      let htmlValidationError = '';
      let payloadInput: string;
      let instructions = customInstructions;
      const activeHtml = previewingHtml || generatedHtml;
      // WAF/security rules block raw HTML (script/style/tag patterns) in the
      // request body, so existing HTML context is sent base64-encoded instead
      // of inline — the Dify workflow decodes it back to HTML on its side.
      const isHtmlPayload = isEditMode && !!activeHtml;

      if (isEditMode && activeHtml) {
        payloadInput = encodeToBase64(activeHtml);
        instructions = customInstructions;
      } else if (isEditMode && generatedCode) {
        payloadInput = generatedCode;
      } else {
        payloadInput = dataToUse || customInstructions;
        if (dataToUse) {
          setInputJson(dataToUse);
          instructions = customInstructions;
        } else {
          instructions = '';
        }
      }

      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const result = await startAgentStream({ baseUrl, aiworker_id: workerId, token });
        currentSessionId = result?.session_id || '';
        if (currentSessionId) setSessionId(currentSessionId);
        else throw new Error('Failed to initialize session. Please refresh and try again.');
      }

      await streamAgentChat({
        baseUrl,
        aiworker_id: workerId,
        session_id: currentSessionId,
        user_input: payloadInput,
        token,
        meta_data: {
          is_edit: isEditMode,
          token,
          instructions,
          template_type: activeHtml ? 'html' : 'jsx',
          ...(isHtmlPayload ? { encoding: 'base64' as const } : {}),
          ...(imageId ? { image_source_id: imageId, image_file_name: imageFileName } : {}),
        },
        onChunk: (chunk) => {
          if (chunk.event === 'node_started') {
            const nodeData = chunk.data;

            // Track node for progress display — use human-readable title first
            const nodeName = nodeData?.title || nodeData?.node_type || nodeData?.node_id;
            if (nodeName && !streamedNodes.includes(nodeName)) {
              streamedNodes.push(nodeName);
              setGenerationProgress(prev => ({
                ...prev,
                [messageId]: { status: 'in_progress', nodes: [...streamedNodes], completedNodes: [...streamedCompletedNodes] },
              }));
            }
          }

          if (chunk.event === 'node_finished') {
            const nodeData = chunk.data;

            // Mark the node as completed (tick mark in UI)
            const finishedNodeName = nodeData?.title || nodeData?.node_type || nodeData?.node_id;
            if (finishedNodeName && !streamedCompletedNodes.includes(finishedNodeName)) {
              streamedCompletedNodes.push(finishedNodeName);
              setGenerationProgress(prev => ({
                ...prev,
                [messageId]: { status: 'in_progress', nodes: [...streamedNodes], completedNodes: [...streamedCompletedNodes] },
              }));
            }

            const nodeHtmlOutput = extractWorkflowHtmlOutput(nodeData);
            if (nodeHtmlOutput && !isHtmlFound) {
              const validation = validateWorkflowHtml(nodeHtmlOutput);
              if (validation.isValid && validation.html) {
                finalHtml = validation.html;
                isHtmlFound = true;
                htmlValidationError = '';
              } else if (validation.error) {
                htmlValidationError = validation.error;
              }
            }

            if (!isCodeFound && !isHtmlFound && nodeData?.outputs?.text && typeof nodeData.outputs.text === 'string') {
              const textContent = nodeData.outputs.text.trim();
              if (isJSXCode(textContent)) {
                finalCode = textContent;
                isCodeFound = true;
              }
            }

            if (!isCodeFound && !isHtmlFound) {
              const outputCode = findJSXCodeInObject(nodeData?.outputs);
              if (outputCode) { finalCode = outputCode; isCodeFound = true; }
            }

            if (!isCodeFound && !isHtmlFound) {
              const inputCode = findJSXCodeInObject(nodeData?.inputs);
              if (inputCode) { finalCode = inputCode; isCodeFound = true; }
            }
          }

          if (chunk.event === 'workflow_finished' || chunk.event === 'message') {
            const htmlOutput = extractWorkflowHtmlOutput(chunk.data);
            if (htmlOutput) {
              const validation = validateWorkflowHtml(htmlOutput);
              if (validation.isValid && validation.html) {
                finalHtml = validation.html;
                isHtmlFound = true;
                isCodeFound = false;
                htmlValidationError = '';
              } else if (validation.isPlainText) {
                textResponse = htmlOutput;
              } else if (validation.error) {
                htmlValidationError = validation.error;
              }
            }

            if (!isHtmlFound && !isCodeFound) {
              const resultCode = findJSXCodeInObject(chunk.data);
              if (resultCode) { finalCode = resultCode; isCodeFound = true; }
            }
          }
        },
        onError: (e) => { throw e; },
      });

      if (isHtmlFound) {
        const validatedHtml = finalHtml;
        setGeneratedHtml(validatedHtml);
        setGeneratedCode(null);
        setPreviewingHtml(validatedHtml);
        setPreviewingCode('');
        setHasGeneratedTemplate(true);
        setIsEditMode(true);

        setGenerationProgress(prev => ({ ...prev, [messageId]: { status: 'done', nodes: streamedNodes, completedNodes: [...streamedCompletedNodes] } }));
        setMessageHtmlTemplates(prev => ({ ...prev, [messageId]: validatedHtml }));
        return;
      }

      if (textResponse) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, content: textResponse } : m));
        setGenerationProgress(prev => ({ ...prev, [messageId]: { status: 'done', nodes: streamedNodes, completedNodes: [...streamedCompletedNodes] } }));
        return;
      }

      if (!isCodeFound) {
        throw new Error(
          htmlValidationError
            ? `Failed to render HTML template: ${htmlValidationError}`
            : 'Failed to generate template. No valid JSX or HTML output found in response.'
        );
      }

      const cleanedCode = cleanGeneratedCode(finalCode);
      setGeneratedCode(cleanedCode);
      setGeneratedHtml(null);
      setPreviewingCode(cleanedCode);
      setPreviewingHtml(null);
      setCodeHistory(prev => [...prev, { id: messageId, code: cleanedCode, timestamp: new Date().toISOString() }]);
      setHasGeneratedTemplate(true);
      setIsEditMode(true);

      // Mark done + store code per message
      setGenerationProgress(prev => ({ ...prev, [messageId]: { status: 'done', nodes: streamedNodes, completedNodes: [...streamedCompletedNodes] } }));
      setMessageTemplates(prev => ({ ...prev, [messageId]: cleanedCode }));

    } catch (error) {
      setGenerationProgress(prev => ({
        ...prev,
        [messageId]: { status: 'error', nodes: streamedNodes, completedNodes: [...streamedCompletedNodes], errorMsg: (error as Error).message },
      }));
    }
  };

  const handleSendClick = async (text: string) => {
    if (!text.trim()) return;

    const ctx = interactionState.isActive
      ? interactionState.selectedText
      : elementComments.length > 0
        ? elementComments[0].elementText
        : undefined;

    setMessages(prev => [...prev, {
      id: uuidv4(),
      role: 'user',
      content: text,
      elementContext: ctx ? ctx.substring(0, 40) : undefined,
      timestamp: new Date().toISOString(),
    }]);

    // Check for @action trigger
    if (actionsList) {
      const sorted = [...actionsList].sort((a: any, b: any) => b.name.length - a.name.length);
      for (const action of sorted) {
        const trigger = `@${action.name}`;
        if (text.toLowerCase().startsWith(trigger.toLowerCase())) {
          const inputVal = text.substring(trigger.length).trim();
          const actionRequiresFile = checkIfActionRequiresFileUpload(action);

          if (actionRequiresFile && uploadedFiles.length === 0) {
            setSelectedAction(action);
            setRequiresFileUpload(true);
            setPendingInputVal(inputVal);
            setMessages(prev => [...prev, {
              id: uuidv4(),
              role: 'assistant',
              content: <p className="text-blue-600">Please upload a file for {action.name}</p>,
              timestamp: new Date().toISOString(),
            }]);
            return;
          }

          const processingMsgId = uuidv4();
          setMessages(prev => [...prev, {
            id: processingMsgId,
            role: 'assistant',
            content: <p className="italic text-default-500">Executing {action.name}...</p>,
            timestamp: new Date().toISOString(),
          }]);

          try {
            const apiSpec = getApiSpec(action);
            const samplePayload = apiSpec?.sample_payload || apiSpec?.payload;
            let integrationsPayload: any = {};

            if (samplePayload) {
              const parsedPayload = safeParsePayload(samplePayload);
              const templatedFields = findTemplatedFields(parsedPayload);
              const variableNames = Object.keys(templatedFields);
              const formData: Record<string, any> = {};

              if (actionRequiresFile && uploadedSourceId) {
                formData['source_id'] = uploadedSourceId;
                formData['source_extension'] = getFileType(uploadedFileName || '');
                if ('file_name' in parsedPayload) formData['file_name'] = uploadedFileName;
              }
              if (variableNames.length > 0 && inputVal) formData[variableNames[0]] = inputVal;

              integrationsPayload = { source_id: action.source_id, payload: createNestedPayloadRecursive(parsedPayload, formData) };
            } else {
              integrationsPayload = { source_id: action.source_id, payload: { claimNumber: inputVal } };
            }

            const response = await executeAction(integrationsPayload).unwrap();
            let finalData = response?.data || response;

            if (finalData) {
              if (Array.isArray(finalData) && finalData.length > 0 && finalData[0]?.claim) {
                finalData = finalData[0].claim;
              } else if (typeof finalData === 'object' && !Array.isArray(finalData)) {
                const cleaned = { ...finalData };
                delete cleaned.status_code;
                delete cleaned.message;
                finalData = cleaned;
              }

              const dataString = JSON.stringify(finalData, null, 2);
              setInputJson(dataString);
              setCurrentActionName(action.name);
              setCurrentActionId(action.aiworker_id);
              setCurrentActionData(action);
              setUploadedFiles([]);
              setUploadedSourceId(null);
              setUploadedFileName(null);
              setSelectedAction(null);
              setRequiresFileUpload(false);

              setMessages(prev => prev.map(msg => msg.id === processingMsgId ? {
                ...msg,
                content: (
                  <div className="space-y-2">
                    <p className="text-green-600 font-medium">✓ Data fetched successfully from {action.name}</p>
                    <pre className="text-xs bg-default-100 p-2 rounded max-h-32 overflow-auto">
                      {dataString.substring(0, 200)}...
                    </pre>
                  </div>
                ),
              } : msg));

              handleGenerate('Generate a comprehensive viewer for this data.', dataString, action);
              return;
            } else {
              setMessages(prev => prev.map(msg => msg.id === processingMsgId ? {
                ...msg,
                content: <p className="text-danger">No data received from {action.name}</p>,
              } : msg));
              return;
            }
          } catch (err) {
            setMessages(prev => prev.map(msg => msg.id === processingMsgId ? {
              ...msg,
              content: <p className="text-danger">Failed to execute action: {(err as Error).message}</p>,
            } : msg));
            return;
          }
        }
      }
    }

    // Handle element comments — clear them silently before generating
    if (isEditMode && elementComments.length > 0) {
      clearElementComments();
    }

    // Detect JSON in user input — strip invisible unicode chars before parsing
    // (zero-width spaces and similar characters cause startsWith('{') to fail)
    const normalizedText = text.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '').trim();
    let detectedJson: string | null = null;
    let generationInstructions = text;

    if (normalizedText.startsWith('{') || normalizedText.startsWith('[')) {
      try {
        JSON.parse(normalizedText);
        detectedJson = normalizedText;
        generationInstructions = 'Generate a comprehensive viewer for this data.';
      } catch { /* not valid JSON, treat as plain instructions */ }
    }

    if (detectedJson) {
      setInputJson(detectedJson);
      setIsEditMode(false);
      setHasGeneratedTemplate(false);
    } else if (hasGeneratedTemplate && (generatedCode || generatedHtml || previewingHtml)) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }

    // Capture and clear image source ID so it's included once in this send
    const capturedImageSourceId = imageSourceId || undefined;
    const capturedImageFileName = uploadedFileName || undefined;
    if (imageSourceId) {
      setImageSourceId(null);
      setUploadedFileName(null);
      setUploadedFiles([]);
    }

    handleGenerate(generationInstructions, detectedJson || undefined, undefined, capturedImageSourceId, capturedImageFileName);
  };

  const loadSampleTemplate = () => {
    const sampleData = {
      insertDate: '2024-01-15T10:30:00Z',
      odometer: '45000',
      claimStatus: 'Approved',
      claimNumber: 'CLI2959871',
      contractNumber: 'CNT-2024-001',
    };
    const sampleCode = `<div className="w-full space-y-6 p-4">
  <div className="space-y-2">
    <h2 className="text-xl font-semibold text-gray-900">Claim</h2>
    <div className="grid grid-cols-2 gap-4">
      <div><label className="text-xs font-medium text-gray-600">Claim number</label><p className="text-sm font-medium text-gray-900">{data?.claimNumber ?? 'N/A'}</p></div>
      <div><label className="text-xs font-medium text-gray-600">Status</label><p className="text-sm font-medium text-gray-900">{data?.claimStatus ?? 'N/A'}</p></div>
    </div>
  </div>
</div>`;

    const dataString = JSON.stringify(sampleData, null, 2);
    setInputJson(dataString);
    setGeneratedCode(sampleCode);
    setCurrentActionName('Sample Template (Test)');
    setHasGeneratedTemplate(true);
    setIsEditMode(true);

    setMessages(prev => [...prev, {
      id: uuidv4(),
      role: 'assistant',
      content: (
        <div className="flex flex-col gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
            <Icon icon="material-symbols:check-circle" /> Sample Template Loaded
          </div>
          <p className="text-xs text-amber-600 mt-1">
            This is a sample for UI testing. Use a real action to save templates.
          </p>
        </div>
      ),
      timestamp: new Date().toISOString(),
    }]);
  };

  // --- Render: Chat Panel (RIGHT) ---
  const renderChatPanel = () => {
    const fontStyle = { fontFamily: 'Poppins, sans-serif' };

    const renderMessage = (msg: (typeof messages)[0]) => {
      if (msg.role === 'user') {
        return (
          <div key={msg.id} className="flex flex-col items-end gap-1 py-2">
            {msg.elementContext && (
              <span
                className="px-1.5 py-0.5 rounded-[6px] border text-xs"
                style={{
                  ...fontStyle,
                  background: '#FDF2F8',
                  borderColor: '#E66DB6',
                  color: '#97005B',
                }}
              >
                {msg.elementContext}
              </span>
            )}
            <div
              className="max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-2.5"
              style={{ background: 'var(--cty-bg-elevated)' }}
            >
              <p className="text-sm text-foreground" style={fontStyle}>
                {typeof msg.content === 'string' ? msg.content : msg.content}
              </p>
            </div>
          </div>
        );
      }

      // Assistant message
      const progress = generationProgress[msg.id];
      if (!progress) {
        // Welcome or non-generation messages
        return (
          <div key={msg.id} className="flex items-start py-2">
            <div
              className="max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-2.5"
              style={{ background: 'var(--cty-bg-elevated)' }}
            >
              <p className="text-sm text-default-600" style={fontStyle}>{msg.content}</p>
            </div>
          </div>
        );
      }

      const msgCode = messageTemplates[msg.id];
      const msgHtml = messageHtmlTemplates[msg.id];
      const isViewingThisMessage =
        (msgCode && previewingCode === msgCode && !previewingHtml) ||
        (msgHtml && previewingHtml === msgHtml);

      return (
        <div key={msg.id} className="flex flex-col items-start gap-2 py-2">

          {/* Progress / Done card */}
          <div
            className="w-full bg-[#F9FAFB] rounded-xl p-4 flex flex-col gap-3 text-[black]"
            style={{ boxShadow: '0px 2px 2px -1px rgba(10,13,18,0.04), 0px 4px 6px -2px rgba(10,13,18,0.03), 0px 12px 16px -4px rgba(10,13,18,0.08)' }}
          >
            {/* Status row */}
            <div className="flex items-center justify-between gap-2">
              {progress.status === 'in_progress' ? (
                <div className="flex items-center gap-2">
                  <Icon icon="eos-icons:loading" className="text-default-600 animate-spin" width={16} />
                  <span className="text-sm text-[#333]" style={fontStyle}>Working...</span>
                </div>
              ) : progress.status === 'done' ? (
                <div className="flex items-center gap-2">
                  <Icon icon="material-symbols:check-circle-rounded" className="text-[#17B26A]" width={16} />
                  <span className="text-sm font-semibold text-[#17B26A]" style={fontStyle}>Done</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Icon icon="material-symbols:error-outline" className="text-danger" width={16} />
                  <span className="text-sm font-semibold text-danger" style={fontStyle}>Failed</span>
                </div>
              )}

              {/* Badge */}
              {progress.status === 'in_progress' && (
                <span
                  className="px-2 py-0.5 rounded-full border text-xs font-medium"
                  style={{ ...fontStyle, background: '#FFFAEB', borderColor: '#FEDF89', color: '#B54708' }}
                >
                  In progress
                </span>
              )}
              {progress.status === 'done' && (
                <span
                  className="px-2 py-0.5 rounded-full border text-xs font-medium"
                  style={{ ...fontStyle, background: '#ECFDF3', borderColor: '#ABEFC6', color: '#067647' }}
                >
                  Completed
                </span>
              )}
              {progress.status === 'error' && (
                <span
                  className="px-2 py-0.5 rounded-full border text-xs font-medium"
                  style={{ ...fontStyle, background: '#FEF3F2', borderColor: '#FECDCA', color: '#B42318' }}
                >
                  Error
                </span>
              )}
            </div>

            {/* Nodes list */}
            {progress.nodes.length > 0 && (
              <div className="flex flex-col gap-1 bg-[#F9FAFB] p-3 rounded">
                <span className="text-xs text-[#333]" style={fontStyle}>Locating the component...</span>
                <ul className="flex flex-col gap-0.5 pl-1">
                  {progress.nodes.slice(-5).map((n, i) => {
                    const isDone = (progress.completedNodes ?? []).includes(n);
                    return (
                      <li key={i} className="text-xs flex items-center gap-1.5" style={fontStyle}>
                        {isDone
                          ? <Icon icon="mdi:check-circle" width={13} className="text-green-500 shrink-0" />
                          : <span className="shrink-0 text-default-500">•</span>
                        }
                        <span className={`truncate ${isDone ? 'text-default-500' : 'text-[#333]'}`}>{n}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Error message */}
            {progress.status === 'error' && progress.errorMsg && (
              <p className="text-xs text-danger" style={fontStyle}>{progress.errorMsg}</p>
            )}

            {/* Progress bar (in-progress) */}
            {progress.status === 'in_progress' && (
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-default-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full animate-pulse w-2/5" />
                </div>
                <Icon icon="material-symbols:stop-circle-outline-rounded" width={20} className="text-default-500 flex-shrink-0" />
              </div>
            )}

            {/* Plain text response from workflow html output */}
            {progress.status === 'done' && !msgCode && !msgHtml && typeof msg.content === 'string' && msg.content !== 'Working on the changes' && (
              <p className="text-sm text-default-600 whitespace-pre-wrap" style={fontStyle}>{msg.content}</p>
            )}

            {/* Click to view (done) */}
            {progress.status === 'done' && (msgCode || msgHtml) && (
              <button
                onClick={() => {
                  if (msgHtml) {
                    setPreviewingHtml(msgHtml);
                    setPreviewingCode('');
                  } else if (msgCode) {
                    setPreviewingCode(msgCode);
                    setPreviewingHtml(null);
                  }
                }}
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${isViewingThisMessage ? 'text-primary' : 'text-primary hover:opacity-70'}`}
                style={fontStyle}
              >
                <Icon icon="mdi:eye-outline" width={16} />
                {isViewingThisMessage ? 'Currently viewing' : 'Click to view'}
              </button>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col h-full min-h-0 bg-content1">
        {/* Messages */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-3">
          {messages.map(renderMessage)}
          <div ref={scrollRef} />
        </div>

        {/* Prompt bar */}
        {!isReadOnly && (
          <div className="flex-shrink-0 px-4 pb-4 pt-2 bg-content1">
            <div className="flex flex-col gap-2.5">
              {/* Context chip — shows active element */}
              {(interactionState.isActive || elementComments.length > 0) && (
                <span
                  className="self-start px-1.5 py-0.5 rounded-[6px] border text-xs"
                  style={{
                    ...fontStyle,
                    background: '#FDF2F8',
                    borderColor: '#E66DB6',
                    color: '#97005B',
                  }}
                >
                  {(interactionState.selectedText || elementComments[0]?.elementText || '').substring(0, 30)}
                </span>
              )}

              {/* Prompt input with send button */}
              <CtyPromptInputWithErrorBoundary
                ref={promptInputRef}
                elementType={promptInputType.TEXTAREA}
                id='chatinput-textarea'
                classNames={{
                  inputWrapper: '!bg-content1 shadow-none border border-default-200 rounded-[10px] px-4 pt-3 pb-2',
                  innerWrapper: 'relative',
                  input: 'text-sm p-0',
                }}
                radius="none"
                variant="flat"
                handleSendClick={handleSendClick}
                placeholder="Start building your blueprint... (e.g., 'Generate a viewer for this data')"
                enableSpeak={false}
                enableSendBtn={true}
                setIsSpeechMode={setIsSpeechMode}
                isSpeechMode={isSpeechMode}
                enableMention={false}
                enableActionMention={false}
                enableFile={true}
                enableImage={false}
                enableTranslation={false}
                enableSearch={false}
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                prefillText={isEditMode && elementComments.length > 0 ? formatElementCommentsAsText(elementComments) : ''}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- Render: Preview content (extracted from nested ternary) ---
  const renderPreviewContent = () => {
    if (!previewingCode && !previewingHtml) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
          <div className="w-14 h-14 mb-4 bg-default-100 flex items-center justify-center">
            <Icon icon="mdi:eye-outline" className="text-default-400" width={28} />
          </div>
          <p className="text-base font-semibold text-[#444446]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Preview Panel
          </p>
          <p className="text-xs text-default-500 mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Your generated template will appear here.
          </p>
        </div>
      );
    }

    if (previewingHtml) {
      return (
        <WorkflowHtmlPreview
          html={previewingHtml}
          editorPreview
          onMessage={(message) => {
            if (message.type === 'metadata' && message.data && typeof message.data === 'object' && !Array.isArray(message.data)) {
              templateMetadataRef.current = { ...(message.data as Record<string, unknown>) };
              return;
            }
            if (message.type === 'formSubmit' || message.type === 'registrationComplete') {
              const pretty = JSON.stringify(message.data ?? message, null, 2);
              setMessages(prev => [...prev, {
                id: uuidv4(),
                role: 'assistant',
                content: (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-[#067647]">Form submitted from preview</p>
                    <pre className="text-xs bg-default-100 p-3 rounded-lg max-h-64 overflow-auto whitespace-pre-wrap break-words">
                      {pretty}
                    </pre>
                  </div>
                ),
                timestamp: new Date().toISOString(),
              }]);
            }
          }}
        />
      );
    }

    return (
      <div className="">
        <UXGenieTemplateRenderer
          code={previewingCode}
          data={inputJson}
          onElementClick={handlePreviewClick}
          containerClassName="w-full max-w-full"
        />
      </div>
    );
  };

  // --- Render: Preview Panel (LEFT) ---
  const renderPreviewPanel = () => (
    <div className="relative h-full min-h-0 bg-[#F2F4F7]">
      {/* Floating action buttons — absolute, no layout impact */}
      {!isReadOnly && (
        <div className="absolute top-0 right-4 z-10">
          <CtyButton
            onPress={() => {
              const actionId = currentActionData?.source_id || currentActionData?.aiworker_id || currentActionData?.id;
              handleSaveTemplate(
                previewingHtml || generatedHtml || previewingCode || generatedCode || undefined,
                inputJson,
                templateTitle || currentActionName || undefined,
                actionId ?? undefined
              );
            }}
            className="rounded-lg text-[12px] font-semibold"
            size="sm"
            variant="solid"
            color='primary'
          >
            Save
          </CtyButton>
        </div>
      )}

      {/* Full-height white card — scrollable template content */}
      <div className="h-full bg-content1 rounded-2xl overflow-y-auto overflow-x-hidden">
        {renderPreviewContent()}
      </div>
    </div>
  );

  return (
    <>
      {/* Save Template Modal */}
      <Modal
        isOpen={isSaveModalOpen}
        onClose={() => { setIsSaveModalOpen(false); setPendingSaveData(null); setTemplateNameInput(''); }}
        size="md"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <span>Save Template</span>
            <span className="text-sm font-normal text-default-500">Enter a name or skip to auto-generate</span>
          </ModalHeader>
          <ModalBody>
            <Input
              label="Template Name"
              placeholder={`e.g., ${worker?.name || 'Template'} 1`}
              value={templateNameInput}
              onChange={(e) => setTemplateNameInput(e.target.value)}
              variant="bordered"
              description={templateNameInput.trim() ? '' : `If left empty, will be named "${worker?.name || 'Template'} [number]"`}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmSaveTemplate(); }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => { setIsSaveModalOpen(false); setPendingSaveData(null); setTemplateNameInput(''); }}>
              Cancel
            </Button>
            <Button color="primary" onPress={confirmSaveTemplate} isLoading={isSaving}>
              {templateNameInput.trim() ? 'Save' : 'Save with Auto-name'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Comment Popover */}
      <CommentModal
        interactionState={interactionState}
        instructionText={instructionText}
        isEditMode={isEditMode}
        onInstructionChange={setInstructionText}
        onSubmit={isEditMode ? handleAddComment : handleApplyInstruction}
        onClose={resetInteraction}
      />

      {/* Split Panel — preview LEFT, chat RIGHT */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ResizableSplitPanel
          leftPanel={renderPreviewPanel()}
          rightPanel={renderChatPanel()}
          defaultLeftWidth={62}
          minLeftWidth={35}
          minRightWidth={28}
        />
      </div>
    </>
  );
};
